// folderWiring.js
//
// Single source of truth for the folder wiring shared by every admin list
// screen (Surveys / Knowledge Checks / Messages / Library). Each screen builds
// one handler set once (in its constructor) and delegates to it, so there is no
// copied handler logic left in the class components.
//
//   this.folder = makeFolderHandlers(this, {
//     category, listKey, selectedKey, emptyTopText, deletedItemsLabel, sortItems,
//   })
//
// The returned object exposes:
//   - getVisibleItems()  -> the rows currently visible in the table
//   - getRenderState()   -> derived render vars (folders block, empty state, ...)
//   - every folder action handler (create / rename / delete / add / move /
//     remove / drag-drop), each closing over the component instance.
//
// Per-screen differences are fully parameterized via `config`; the formulas
// below are unified so the same code path serves all four screens (props/state
// that a given screen lacks — e.g. `showTrivvyListOnly` on Library, or
// `isSearchActive` on the trivia screens — read as undefined and drop out).

import * as folderService from './folderService'
import { toastContainer } from '../../../utils/toast'

export const makeFolderHandlers = (instance, config) => {
  const {
    category,
    listKey,
    selectedKey,
    emptyTopText,
    deletedItemsLabel,
    // Optional. Called after a top-level mutation for screens whose list is
    // PAGINATED: moving an item into a folder removes it from the top-level
    // set (the list query is scoped to folderId: null), which shifts every
    // later page's skip offset. Screens that load their whole list up front
    // don't pass this and are unaffected.
    onListMutated,
    sortItems,
  } = config

  const clearSelected = () => {
    const patch = {}
    if (selectedKey) {
      patch[selectedKey] = []
    }
    return patch
  }

  // Pull the API error message (BE error responses carry `message`), else a
  // sensible fallback.
  const folderErrorMessage = (err, fallback) => {
    const body = err && err.response && err.response.data
    if (body && body.message) {
      return body.message
    }
    if (body && body.data && body.data.message) {
      return body.data.message
    }
    return fallback
  }

  // Shared rejection handler for folder API calls: toast the error and optionally
  // reset some state (e.g. close a modal) so the user is never left stuck.
  const handleFolderError = (fallback, cleanup) => (err) => {
    console.log('folder operation failed', err)
    if (cleanup) {
      instance.setState(cleanup)
    }
    toastContainer(folderErrorMessage(err, `Could not ${fallback}. Please try again.`), 'error')
  }

  const applyFolderState = (result, extra) => {
    const folders = (result && result.folders) || []
    instance.setState({
      folderList: folders,
      folderMembership: folderService.buildCountMap(folders),
      ...(extra || {}),
    })
  }

  // Re-fetch the open folder's contents after a mutation so the folder view
  // reflects the change (add/remove).
  const reloadActiveFolder = () => {
    const { activeFolder } = instance.state
    if (!activeFolder) {
      return
    }
    folderService
      .getFolderContents(category, activeFolder._id)
      .then((items) => {
        if (
          instance.state.activeFolder &&
          instance.state.activeFolder._id === activeFolder._id
        ) {
          instance.setState({ folderContents: items })
        }
      })
      .catch((err) => {
        console.log('folder contents reload failed', err)
      })
  }

  // Fetch the folder list (with fresh item counts) and store it. Only call this
  // when the list is actually on screen — at the top level, or on exit from a
  // folder — never on an in-folder mutation.
  const refreshFolders = (extra) =>
    folderService
      .getFolders(category)
      .then((folders) => {
        applyFolderState({ folders }, extra)
        return folders
      })
      .catch(handleFolderError('load folders'))

  // After a mutation, refresh only what the user is currently looking at:
  //  - inside a folder: that folder's CONTENTS. The folder list is off-screen,
  //    so we deliberately skip get-folders here; it refreshes when the folder is
  //    closed (breadcrumb back) or the tab is re-opened.
  //  - at the top level: the folder LIST, with fresh counts.
  const refreshAfterMutation = (extra) => {
    if (instance.state.activeFolder) {
      if (extra) {
        instance.setState(extra)
      }
      reloadActiveFolder()
    } else {
      refreshFolders(extra)
      if (typeof onListMutated === 'function') {
        onListMutated()
      }
    }
  }

  // Patch a single item inside the open folder's contents in place, so a row
  // action taken from the folder view (publish, end, rename, …) shows the new
  // state immediately — mirroring how the main list patches itself. No-op when no
  // folder is open or the item isn't in it.
  const patchFolderItem = (updated) => {
    if (!updated || !updated._id) {
      return
    }
    const { activeFolder, folderContents } = instance.state
    if (!activeFolder || !folderContents) {
      return
    }
    const idx = folderContents.findIndex((x) => x && x._id === updated._id)
    if (idx === -1) {
      return
    }
    const next = [...folderContents]
    next[idx] = updated
    instance.setState({ folderContents: next })
  }

  // Drop a deleted item from the open folder's contents so the folder view
  // updates without a re-fetch. No-op when no folder is open.
  const removeFolderItem = (id) => {
    const { activeFolder, folderContents } = instance.state
    if (!activeFolder || !folderContents) {
      return
    }
    instance.setState({
      folderContents: folderContents.filter((x) => x && x._id !== id),
    })
  }

  // Keep an item's folderId membership current in the in-memory lists (the main
  // list + the open folder's contents) after an add/remove, so the row submenu's
  // "already in this folder" greying — and the top-level list — stay accurate
  // without re-fetching. Move model (TBV-726): an item has exactly one folderId.
  const applyItemMembership = (itemId, folderId, isMember) => {
    const nextFolderId = isMember ? folderId : null
    const targetKey = nextFolderId ? String(nextFolderId) : null
    const patchArr = (arr) => {
      if (!arr || !arr.length) {
        return null
      }
      let changed = false
      const next = arr.map((it) => {
        if (!it || it._id !== itemId) {
          return it
        }
        const currentKey = it.folderId ? String(it.folderId) : null
        if (currentKey !== targetKey) {
          changed = true
          return { ...it, folderId: nextFolderId }
        }
        return it
      })
      return changed ? next : null
    }
    const stateUpdate = {}
    const listNext = patchArr(instance.state[listKey])
    if (listNext) {
      stateUpdate[listKey] = listNext
    }
    const contentsNext = patchArr(instance.state.folderContents)
    if (contentsNext) {
      stateUpdate.folderContents = contentsNext
    }
    if (Object.keys(stateUpdate).length) {
      instance.setState(stateUpdate)
    }
  }

  // Rows currently visible in the table: the open folder's items when a folder
  // is open, the search results while searching (Library), otherwise the
  // top-level (un-foldered) items.
  const getVisibleItems = () => {
    const list = instance.state[listKey] || []
    const { activeFolder, folderContents, isSearchActive, searchResults } = instance.state
    if (instance.props.showTrivvyListOnly) {
      return list
    }
    if (isSearchActive && searchResults !== null) {
      return searchResults
    }
    if (activeFolder) {
      // Folder view shows the folder's fetched contents (the paginated main
      // list may not hold every member).
      return folderContents || []
    }
    // Move model (TBV-726): the top level shows only un-foldered items — a
    // foldered item lives under its folder, not in the main list. The BE list
    // already excludes foldered items; filtering here also drops one
    // optimistically the moment it's added to a folder, before any re-fetch.
    return list.filter((it) => it && !it.folderId)
  }

  const getRenderState = () => {
    const list = instance.state[listKey] || []
    const { activeFolder, folderContents } = instance.state

    const inFolderView = !!activeFolder && !instance.props.showTrivvyListOnly
    const showFolders =
      !instance.props.showTrivvyListOnly &&
      !inFolderView &&
      !instance.state.isSearchActive
    let sortedFolders = showFolders
      ? folderService.sortFolders(
          instance.state.folderList,
          instance.props.sortParams.sortBy,
          instance.props.sortParams.sortOrder
        )
      : []
    // TBV-726 (item 4): a just-created folder floats to the TOP so the user sees
    // it was created — even under a sort (e.g. Name A–Z) that would otherwise
    // place it elsewhere. One-shot: the consumer clears it via clearJustCreatedPin()
    // on the next sort change, so returning to the creation-time sort can't re-pin
    // it. It survives non-sort refreshes (add/move/rename) until then.
    const justCreatedId = instance.state.justCreatedFolderId
    if (justCreatedId && sortedFolders.length > 1) {
      const idx = sortedFolders.findIndex((f) => f && f._id === justCreatedId)
      if (idx > 0) {
        sortedFolders = [
          sortedFolders[idx],
          ...sortedFolders.slice(0, idx),
          ...sortedFolders.slice(idx + 1),
        ]
      }
    }
    // The rendered rows carry the sort, not state[listKey] — search results and
    // folder contents never pass through the screens' handleSortChange.
    const visibleItems = getVisibleItems()
    const sortedItems = sortItems ? sortItems(visibleItems) : visibleItems
    // Rows still in state belong to the OLD sort while a server-sorted refetch
    // is in flight, so showing them paints a wrong order for a whole round trip.
    const isSortReloading = !!instance.state.isSortReloading
    let displayList = sortedItems
    if (isSortReloading) {
      displayList = []
    }
    // Candidates for the "add existing to folder" picker: un-foldered items not
    // already in the open folder (move model — an item has at most one folder).
    const unfolderedCandidates =
      inFolderView && activeFolder
        ? folderService.deriveAddableItems(list, folderContents, activeFolder._id)
        : []
    const activeFolderCount =
      inFolderView && activeFolder ? (folderContents || []).length : 0
    // Suppressed while reloading: the blanked displayList would otherwise flash
    // "no trivvys created" under the spinner for the whole round trip.
    let showEmptyMessage = false
    if (!isSortReloading) {
      if (inFolderView) {
        showEmptyMessage = (folderContents || []).length === 0
      } else {
        showEmptyMessage = displayList.length === 0 && sortedFolders.length === 0
      }
    }
    const folderNoun = folderService.getFolderNoun(category)
    const emptyMessageText = inFolderView
      ? `No ${folderNoun} in this folder yet. Add ${folderNoun} to get started.`
      : emptyTopText

    return {
      inFolderView,
      showFolders,
      sortedFolders,
      displayList,
      unfolderedCandidates,
      activeFolderCount,
      showEmptyMessage,
      emptyMessageText,
      isSortReloading,
    }
  }

  const loadFolders = () => {
    if (instance.props.showTrivvyListOnly) {
      return
    }
    refreshFolders().then((folders) => {
      // The edit-page crumb links back here with ?folderId=; open that folder.
      const params = new URLSearchParams(window.location.search)
      const wanted = params.get(folderService.FOLDER_QUERY_PARAM)
      if (!wanted) {
        return
      }
      const match = (folders || []).find(
        (f) => f && String(f._id) === String(wanted)
      )
      if (match) {
        openFolder(match)
      }
    })
  }

  const openFolderNameModalCreate = () => {
    instance.setState({
      folderNameModal: { open: true, mode: 'create', folder: null },
    })
  }

  const openFolderNameModalRename = (folder) => {
    instance.setState({
      folderNameModal: { open: true, mode: 'rename', folder: folder },
    })
  }

  const closeFolderNameModal = () => {
    instance.setState({
      folderNameModal: { open: false, mode: 'create', folder: null },
    })
  }

  const handleFolderNameSave = (name) => {
    const { mode, folder } = instance.state.folderNameModal
    if (mode === 'rename' && folder) {
      folderService.renameFolder(category, folder._id, name).then(() => {
        const closeModal = {
          folderNameModal: { open: false, mode: 'create', folder: null },
        }
        const active = instance.state.activeFolder
        if (active && active._id === folder._id) {
          // Renaming the folder we're inside: update the breadcrumb name only.
          // The folder list is off-screen, so no get-folders here — it refreshes
          // when we go back (closeFolder).
          instance.setState({ ...closeModal, activeFolder: { ...active, name: name } })
        } else {
          // Top-level rename: refresh the list so the row shows the new name.
          refreshFolders(closeModal)
        }
      }).catch(
        handleFolderError('rename the folder', {
          folderNameModal: { open: false, mode: 'create', folder: null },
        })
      )
    } else {
      const pendingItem = instance.state.folderNameModal.pendingItem
      folderService.createFolder(category, name).then((result) => {
        const newFolderId =
          result && result.folder && result.folder._id
            ? result.folder._id
            : null
        const closeExtra = {
          folderNameModal: { open: false, mode: 'create', folder: null },
          // Pin the new folder to the top of the list (TBV-726 item 4). Released
          // by the consumer's clearJustCreatedPin() on the next sort change, so
          // it's a one-shot that survives refreshes but not a re-sort.
          justCreatedFolderId: newFolderId,
        }
        // Submenu "+ Create new folder…": add the stashed item into the folder
        // that was just created.
        if (pendingItem && result.folder && result.folder._id) {
          return folderService
            .addItemToFolder(category, result.folder._id, pendingItem._id)
            .then(() => {
              applyItemMembership(pendingItem._id, result.folder._id, true)
              refreshAfterMutation({ ...closeExtra, ...clearSelected() })
              toastContainer('Folder created — item added', 'success')
            })
        }
        refreshAfterMutation(closeExtra)
        toastContainer('Folder created', 'success')
        return null
      }).catch(
        handleFolderError('create the folder', {
          folderNameModal: { open: false, mode: 'create', folder: null },
        })
      )
    }
  }

  const handleDeleteFolder = (folder) => {
    // Open the confirmation dialog; the actual delete runs in confirmDeleteFolder.
    instance.setState({ deleteFolderModal: { open: true, folder: folder } })
  }

  const closeDeleteFolder = () => {
    instance.setState({ deleteFolderModal: { open: false, folder: null } })
  }

  const confirmDeleteFolder = () => {
    const modal = instance.state.deleteFolderModal
    const target = modal && modal.folder ? modal.folder : null
    if (!target) {
      return
    }
    folderService.deleteFolder(category, target._id).then(() => {
      const extra = { deleteFolderModal: { open: false, folder: null } }
      if (
        instance.state.activeFolder &&
        instance.state.activeFolder._id === target._id
      ) {
        extra.activeFolder = null
        extra.folderContents = []
      }
      // Delete always changes the list (and, if the open folder was deleted,
      // drops us back to it), so refresh the folder list here.
      refreshFolders(extra)
      // Deleting the folder you are inside is a second exit path, and it never
      // goes through closeFolder — so a deferred sort reset must be drained here.
      if (
        extra.activeFolder === null &&
        typeof instance.drainPendingListReset === 'function'
      ) {
        instance.drainPendingListReset()
      }
      toastContainer(
        `The Folder was deleted. The folder content is available in the list of ${deletedItemsLabel}.`,
        'success'
      )
    }).catch(
      handleFolderError('delete the folder', {
        deleteFolderModal: { open: false, folder: null },
      })
    )
  }

  const openFolder = (folder) => {
    instance.setState({
      activeFolder: folder,
      folderContents: [],
      ...clearSelected(),
    })
    folderService
      .getFolderContents(category, folder._id)
      .then((items) => {
        if (
          instance.state.activeFolder &&
          instance.state.activeFolder._id === folder._id
        ) {
          instance.setState({ folderContents: items })
        }
      })
      .catch(handleFolderError('load the folder contents'))
  }

  const closeFolder = () => {
    instance.setState({
      activeFolder: null,
      folderContents: [],
      ...clearSelected(),
    })
    // Back to the list: refresh folders so counts/names reflect any changes made
    // while inside (in-folder mutations deliberately skip get-folders).
    refreshFolders()
    // A sort change made inside the folder deferred its list reset; run it now
    // that the paginated list is on screen again (only the paged lists define it).
    if (typeof instance.drainPendingListReset === 'function') {
      instance.drainPendingListReset()
    }
  }

  // Add/move an item to a folder directly from the row 3-dots submenu — no picker
  // modal. Move model (TBV-726): $set folderId on the BE moves the item to the
  // target folder, automatically leaving any folder it was already in — so there
  // is no separate "remove from current" step.
  const moveItemToFolder = (item, folderId, isMove) => {
    instance.setState({ optionsPopoverOpen: null })
    folderService
      .addItemToFolder(category, folderId, item._id)
      .then(() => {
        applyItemMembership(item._id, folderId, true)
        refreshAfterMutation({ ...clearSelected() })
        toastContainer(isMove ? 'Moved to folder' : 'Added to folder', 'success')
      })
      .catch(handleFolderError('move the item to the folder'))
  }

  // "+ Create new folder…" from the submenu: open the create modal and stash
  // the item so handleFolderNameSave drops it into the new folder on create.
  const createFolderForItem = (item) => {
    instance.setState({
      optionsPopoverOpen: null,
      folderNameModal: {
        open: true,
        mode: 'create',
        folder: null,
        pendingItem: item,
      },
    })
  }

  const handleRemoveItemFromFolder = (itemId) => {
    instance.setState({ optionsPopoverOpen: null })
    const folderId = instance.state.activeFolder && instance.state.activeFolder._id
    if (!folderId) {
      return
    }
    folderService
      .removeItemFromFolder(category, folderId, itemId)
      .then(() => {
        applyItemMembership(itemId, folderId, false)
        // Always in-folder → refresh the open folder's contents only.
        refreshAfterMutation({ ...clearSelected() })
        toastContainer('Removed from folder', 'success')
      })
      .catch(handleFolderError('remove the item from the folder'))
  }

  const openSelectTrivvysModal = () => {
    instance.setState({ selectTrivvysModalOpen: true })
  }

  const closeSelectTrivvysModal = () => {
    instance.setState({ selectTrivvysModalOpen: false })
  }

  // Grow the "add existing" picker on scroll by loading the next page into the
  // shared list state — the SAME pagination the list page uses (its
  // handleBottomScroll appends the next page to the list the picker reads from).
  // Only the paginated screens page in; Messages and Library fetch their full
  // list up front, so there is nothing more to load.
  const loadMoreCandidates = () => {
    if (
      (category === 'SURVEY' || category === 'KNOWLEDGE_CHECK') &&
      typeof instance.handleBottomScroll === 'function'
    ) {
      instance.handleBottomScroll()
    }
  }

  const handleAddExistingToFolder = (itemIds) => {
    const folderId = instance.state.activeFolder && instance.state.activeFolder._id
    if (!folderId || !itemIds || !itemIds.length) {
      closeSelectTrivvysModal()
      return
    }
    folderService
      .addItemsToFolder(category, folderId, itemIds)
      .then(() => {
        itemIds.forEach((id) => applyItemMembership(id, folderId, true))
        // Always in-folder → refresh the open folder's contents only.
        refreshAfterMutation({ selectTrivvysModalOpen: false })
        // ...but these items just LEFT the top-level set, so a paginated base
        // list must re-sync too. refreshAfterMutation only fires onListMutated
        // on its top-level branch, and this handler is by definition in-folder,
        // so call it here as well — otherwise the base list's skip offsets stay
        // shifted by the number of items added and that many messages are
        // stepped over on the next scroll and never seen again.
        if (typeof onListMutated === 'function') {
          onListMutated()
        }
        toastContainer('Added to folder', 'success')
      })
      .catch(
        handleFolderError('add items to the folder', {
          selectTrivvysModalOpen: false,
        })
      )
  }

  // ---- Native HTML5 drag-and-drop --------------------------------------

  const handleTrivvyDragStart = (e, itemId) => {
    instance.setState({ draggingItemId: itemId })
    if (e && e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
      try {
        e.dataTransfer.setData('text/plain', itemId)
      } catch (err) {
        // some browsers throw if setData is unavailable — ignore for the mock
      }
      // The default browser drag image snapshots the whole row node, which
      // includes the hover tooltip (react-tooltip, position:fixed, showing the
      // trivvy name) sitting above the row and the card's box-shadow — so the
      // ghost floats with a stray white/shadowed sliver above the card. Build a
      // controlled drag image instead: clone the row, drop any tooltip nodes,
      // flatten the shadow, and clip to the card's exact box so the ghost is
      // only the card. Appended under the same parent so the container-scoped
      // styles still apply, then removed once the snapshot is taken.
      const node = e.currentTarget
      if (
        node &&
        node.parentNode &&
        typeof e.dataTransfer.setDragImage === 'function'
      ) {
        const rect = node.getBoundingClientRect()
        const ghost = node.cloneNode(true)
        const tips = ghost.querySelectorAll('.__react_component_tooltip')
        for (let i = 0; i < tips.length; i++) {
          if (tips[i].parentNode) {
            tips[i].parentNode.removeChild(tips[i])
          }
        }
        ghost.style.boxShadow = 'none'
        ghost.style.margin = '0'
        ghost.style.width = `${rect.width}px`
        ghost.style.height = `${rect.height}px`
        ghost.style.overflow = 'hidden'
        ghost.style.background = '#ffffff'
        ghost.style.position = 'fixed'
        ghost.style.top = '-10000px'
        ghost.style.left = '0'
        ghost.style.pointerEvents = 'none'
        node.parentNode.appendChild(ghost)
        e.dataTransfer.setDragImage(
          ghost,
          e.clientX - rect.left,
          e.clientY - rect.top
        )
        setTimeout(() => {
          if (ghost.parentNode) {
            ghost.parentNode.removeChild(ghost)
          }
        }, 0)
      }
    }
  }

  const handleTrivvyDragEnd = () => {
    instance.setState({ draggingItemId: null, dragOverFolderId: null })
  }

  const handleFolderDragOver = (folderId) => {
    if (instance.state.dragOverFolderId !== folderId) {
      instance.setState({ dragOverFolderId: folderId })
    }
  }

  const handleFolderDragLeave = (folderId) => {
    if (instance.state.dragOverFolderId === folderId) {
      instance.setState({ dragOverFolderId: null })
    }
  }

  // Release the "just created" pin (TBV-726 item 4) so the folder no longer floats
  // to the top. The consumer calls this when the sort changes, making the pin a
  // one-shot; guarded so it never fires a needless re-render.
  const clearJustCreatedPin = () => {
    if (instance.state.justCreatedFolderId) {
      instance.setState({ justCreatedFolderId: null })
    }
  }

  const handleFolderDrop = (folder) => {
    const itemId = instance.state.draggingItemId
    instance.setState({ dragOverFolderId: null, draggingItemId: null })
    if (itemId) {
      folderService
        .addItemToFolder(category, folder._id, itemId)
        .then(() => {
          applyItemMembership(itemId, folder._id, true)
          // Drag-drop targets a folder row, which only shows at the top level →
          // refresh the folder list so its count updates.
          refreshAfterMutation({ ...clearSelected() })
          toastContainer('Added to folder', 'success')
        })
        .catch(handleFolderError('add the item to the folder'))
    }
  }

  return {
    category,
    getVisibleItems,
    getRenderState,
    applyFolderState,
    patchFolderItem,
    removeFolderItem,
    reloadActiveFolder,
    loadFolders,
    openFolderNameModalCreate,
    openFolderNameModalRename,
    clearJustCreatedPin,
    closeFolderNameModal,
    handleFolderNameSave,
    handleDeleteFolder,
    closeDeleteFolder,
    confirmDeleteFolder,
    openFolder,
    closeFolder,
    moveItemToFolder,
    createFolderForItem,
    handleRemoveItemFromFolder,
    openSelectTrivvysModal,
    closeSelectTrivvysModal,
    loadMoreCandidates,
    handleAddExistingToFolder,
    handleTrivvyDragStart,
    handleTrivvyDragEnd,
    handleFolderDragOver,
    handleFolderDragLeave,
    handleFolderDrop,
  }
}

export default makeFolderHandlers
